export interface ImportReadinessMissingItem {
  sheet?: string
  sheetName?: string
  row?: number | string
  rowNumber?: number | string
  field?: string
  column?: string
  fieldName?: string
  issue?: string
  error?: string
  message?: string
}

export interface ImportReadiness {
  canProceed?: boolean
  summaryMessage?: string
  missingItems?: ImportReadinessMissingItem[]
  /** Non-blocking: rows the importer saves with a gap. `canProceed` stays true. */
  warningMessage?: string
  warnings?: ImportReadinessMissingItem[]
}

export interface NormalizedValidationItem {
  sheet: string
  row: string
  field: string
  issue: string
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

export function extractImportReadiness(responseData: unknown): ImportReadiness | null {
  const data = asRecord(responseData)
  if (!data) return null

  const nested = asRecord(data.result)
  const readiness = (data.importReadiness ?? nested?.importReadiness) as ImportReadiness | undefined
  return readiness ?? null
}

export function extractBulkImportValidation(responseData: unknown): unknown {
  const data = asRecord(responseData)
  if (!data) return null

  const nested = asRecord(data.result)
  return data.bulkImportValidation ?? nested?.bulkImportValidation ?? null
}

export function isInstituteExcelUploadResponse(responseData: unknown): boolean {
  const data = asRecord(responseData)
  if (!data) return false

  const result = asRecord(data.result) ?? data
  return Boolean(result.bulkImportPayload || result.importReadiness || result.bulkImportValidation)
}

function flattenBulkImportValidation(validation: unknown): ImportReadinessMissingItem[] {
  if (!validation) return []
  if (Array.isArray(validation)) return validation as ImportReadinessMissingItem[]

  const record = asRecord(validation)
  if (!record) return []

  const items: ImportReadinessMissingItem[] = []
  Object.entries(record).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry && typeof entry === 'object') {
          items.push(entry as ImportReadinessMissingItem)
        } else {
          items.push({field: key, issue: String(entry)})
        }
      })
    }
  })
  return items
}

export function normalizeValidationItems(
  importReadiness: ImportReadiness | null | undefined,
  bulkImportValidation: unknown
): NormalizedValidationItem[] {
  const fromReadiness = Array.isArray(importReadiness?.missingItems)
    ? importReadiness.missingItems
    : []
  const fromBulk = flattenBulkImportValidation(bulkImportValidation)
  const source = fromReadiness.length > 0 ? fromReadiness : fromBulk

  return source.map((item) => ({
    sheet: String(item.sheet ?? item.sheetName ?? '—'),
    row:
      item.row != null ? String(item.row) : item.rowNumber != null ? String(item.rowNumber) : '—',
    field: String(item.field ?? item.column ?? item.fieldName ?? '—'),
    issue: String(item.issue ?? item.error ?? item.message ?? '—'),
  }))
}
